/**
 * Signed-in sessions kept on this device. A shared iPad can hold one student session and one teacher
 * session at a time; "Sign out / switch student" clears the student one. Tokens in localStorage are
 * allowed by the brief. Nothing about the quiz is ever stored here.
 */
import { useSyncExternalStore } from 'react';
import { isRecord, local, session as tabStore } from './storage';

/** Per-student things this tab keeps (order drafts, journal drafts, the quiz-done mark). */
const STUDENT_TAB_KEYS = ['sov.drafts.', 'sov.journal.', 'sov.quizDone.'] as const;

export interface StudentSession {
  kind: 'student';
  worldId: string;
  token: string;
  studentId: string;
  name: string;
  classCode: string;
  className: string;
}

export interface TeacherSession {
  kind: 'teacher';
  worldId: string;
  token: string;
  classCode: string;
  className: string;
  /** The secret teacher key. Lets this device sign in again if the token is revoked. */
  teacherKey: string | null;
}

export type Session = StudentSession | TeacherSession;

const STUDENT_KEY = 'sov.student.v1';
const TEACHER_KEY = 'sov.teacher.v1';

const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

function isStudent(v: unknown): v is StudentSession {
  return isRecord(v) && v.kind === 'student' && str(v.worldId) && str(v.token) && str(v.studentId) && str(v.name) && typeof v.classCode === 'string' && typeof v.className === 'string';
}

function isTeacher(v: unknown): v is TeacherSession {
  return (
    isRecord(v) &&
    v.kind === 'teacher' &&
    str(v.worldId) &&
    str(v.token) &&
    typeof v.classCode === 'string' &&
    typeof v.className === 'string' &&
    (v.teacherKey === null || typeof v.teacherKey === 'string')
  );
}

interface Snapshot {
  student: StudentSession | null;
  teacher: TeacherSession | null;
}

let snapshot: Snapshot = {
  student: local.getJSON(STUDENT_KEY, isStudent),
  teacher: local.getJSON(TEACHER_KEY, isTeacher),
};
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Another tab signed in or out: follow it.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STUDENT_KEY && e.key !== TEACHER_KEY && e.key !== null) return;
    snapshot = { student: local.getJSON(STUDENT_KEY, isStudent), teacher: local.getJSON(TEACHER_KEY, isTeacher) };
    emit();
  });
}

export const sessions = {
  get: (): Snapshot => snapshot,
  setStudent(s: StudentSession | null) {
    // Signing out (or a different student signing in) wipes the last student's drafts from this tab.
    const prev = snapshot.student;
    if (!s || (prev && (prev.studentId !== s.studentId || prev.worldId !== s.worldId))) tabStore.removePrefixed(STUDENT_TAB_KEYS);
    if (s) local.setJSON(STUDENT_KEY, s);
    else local.remove(STUDENT_KEY);
    snapshot = { ...snapshot, student: s };
    emit();
  },
  setTeacher(s: TeacherSession | null) {
    if (s) local.setJSON(TEACHER_KEY, s);
    else local.remove(TEACHER_KEY);
    snapshot = { ...snapshot, teacher: s };
    emit();
  },
  /** Replaces only the token (after a teacher re-login). */
  updateTeacherToken(token: string) {
    if (!snapshot.teacher) return;
    sessions.setTeacher({ ...snapshot.teacher, token });
  },
  subscribe,
};

/** The sessions stored on this device (re-renders when they change, including from other tabs). */
export function useSession(): Snapshot {
  return useSyncExternalStore(subscribe, sessions.get, sessions.get);
}
